// aiCandidateAdapter — pure helpers between scenePackage `CandidateFact`
// and the WP-9 AI review surfaces.
//
// All UI-affecting logic that the spec acceptance tests exercise lives
// here so the test suite can run without a DOM. The card and its
// subcomponents call these helpers; they never re-implement them inline.
//
// References:
//   - `frontend/src/app/scenePackage.ts` — `CandidateFact` shape.
//   - `frontend/src/app/sceneSelection.ts` — `TOOL_CARD_REQUEST_EVENT`,
//     `dispatchToolCardRequest`.
//   - masterplan §7.9, §8.9 — surface scope and acceptance tests.

import type { CandidateFact } from "@/app/scenePackage";
import {
  TOOL_CARD_REQUEST_EVENT,
  type SceneSelection,
  type ToolCardRequestDetail,
} from "@/app/sceneSelection";
import type {
  AiClientResult,
  AiReviewClient,
  AiSemanticSearchHit,
} from "./aiClient";

// ---------------------------------------------------------------------------
// View-model
// ---------------------------------------------------------------------------

/** Stable filter chips exposed by the card. Cumulative when multiple
 *  chips are active. */
export type AiCandidateFilterChip =
  | "verified"
  | "low-confidence"
  | "needs-review";

/** Threshold under which a candidate's confidence is considered low. The
 *  masterplan §7.9 calls for a "low-confidence" filter; the threshold is
 *  conservative until the platform exposes a per-tenant override. */
export const AI_LOW_CONFIDENCE_THRESHOLD = 0.7;

export interface AiCandidateRowVM {
  readonly id: string;
  readonly subjectNodeId: string;
  readonly predicate: string;
  /** Display value: `literalValue` if present, else `objectNodeId`, else
   *  the empty string so callers can render a `<Banner>` row instead. */
  readonly displayValue: string;
  readonly factType: string;
  readonly source: CandidateFact["source"];
  readonly sourceLabel: "Source-of-truth" | "AI candidate" | "Rule candidate" | "User decision";
  readonly sourceIcon: "fact_check" | "auto_awesome";
  /** Two-decimal mono confidence, e.g. "0.92". When the confidence is
   *  missing or non-finite this is the empty string. */
  readonly confidenceLabel: string;
  /** Numeric confidence rounded to 2 decimals; null when missing. */
  readonly confidence: number | null;
  readonly reviewStatus: CandidateFact["reviewStatus"];
  readonly modelVersion: string | null;
  readonly ruleVersion: string | null;
  readonly datasetHashShort: string | null;
  readonly timestamp: string | null;
  /** True when the candidate cannot be safely rendered as an actionable
   *  row — either confidence or source attribution is missing.  Per the
   *  WP-9 design rule the row collapses to a `<Banner severity="warning">`
   *  in this case. */
  readonly missingTrust: boolean;
  /** Original record kept on the VM for actions / pivots. */
  readonly raw: CandidateFact;
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/** Format a confidence number as a two-decimal mono string, e.g.
 *  `formatConfidence(0.9123)` → `"0.91"`.  Returns the empty string when
 *  the value is missing or non-finite. */
export function formatConfidence(value: unknown): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "";
  return value.toFixed(2);
}

/** Resolve the rounded mono confidence value for a candidate. Returns
 *  null when the field is missing or non-finite. */
export function readConfidence(candidate: CandidateFact): number | null {
  const c = candidate.confidence;
  if (typeof c !== "number" || !Number.isFinite(c)) return null;
  return Math.round(c * 100) / 100;
}

/** Pick the source attribution icon for a candidate. `source-truth` is
 *  the only kind that earns the `fact_check` icon. */
export function readSourceIcon(
  candidate: CandidateFact,
): "fact_check" | "auto_awesome" {
  return candidate.source === "source-truth" ? "fact_check" : "auto_awesome";
}

export function readSourceLabel(
  candidate: CandidateFact,
): AiCandidateRowVM["sourceLabel"] {
  switch (candidate.source) {
    case "source-truth":
      return "Source-of-truth";
    case "ai-inferred":
      return "AI candidate";
    case "rule-inferred":
      return "Rule candidate";
    case "user-approved":
    case "user-rejected":
      return "User decision";
    default:
      return "AI candidate";
  }
}

/** Read a short hash from the provenance record. Looks at the conventional
 *  keys (`datasetHash`, `dataset`, `evidenceHash`) and returns the first
 *  10 characters when present. */
export function readDatasetHashShort(candidate: CandidateFact): string | null {
  const provenance = candidate.provenance;
  if (!provenance || typeof provenance !== "object") return null;
  const keys = ["datasetHash", "dataset", "evidenceHash", "hash"];
  for (const key of keys) {
    const value = (provenance as Record<string, unknown>)[key];
    if (typeof value === "string" && value.trim().length > 0) {
      return value.slice(0, 10);
    }
  }
  return null;
}

/** Read the most informative model identifier for a candidate. Falls back
 *  through `modelVersion`, `ruleVersion`, `provenance.model`, and
 *  `provenance.modelId`. Returns null when none are populated. */
export function readModelIdentifier(candidate: CandidateFact): string | null {
  if (candidate.modelVersion && candidate.modelVersion.trim().length > 0) {
    return candidate.modelVersion;
  }
  if (candidate.ruleVersion && candidate.ruleVersion.trim().length > 0) {
    return candidate.ruleVersion;
  }
  const provenance = candidate.provenance;
  if (!provenance || typeof provenance !== "object") return null;
  const record = provenance as Record<string, unknown>;
  for (const key of ["model", "modelId", "model_version", "ruleId"]) {
    const value = record[key];
    if (typeof value === "string" && value.trim().length > 0) return value;
  }
  return null;
}

export function readTimestamp(candidate: CandidateFact): string | null {
  return (
    (candidate.updatedAt && candidate.updatedAt.trim().length > 0
      ? candidate.updatedAt
      : null) ??
    (candidate.createdAt && candidate.createdAt.trim().length > 0
      ? candidate.createdAt
      : null) ??
    null
  );
}

/** True when a candidate cannot be rendered as a trustable row — the WP-9
 *  design rule says: every row must show BOTH confidence AND source. */
export function isMissingTrust(candidate: CandidateFact): boolean {
  const hasConfidence = readConfidence(candidate) != null;
  const hasSource =
    typeof candidate.source === "string" && candidate.source.trim().length > 0;
  return !(hasConfidence && hasSource);
}

/** Derive the per-row view-model used by the card. Pure; safe to call
 *  in tests. */
export function toCandidateRowVM(candidate: CandidateFact): AiCandidateRowVM {
  const confidence = readConfidence(candidate);
  const displayValue =
    candidate.literalValue && candidate.literalValue.trim().length > 0
      ? candidate.literalValue
      : candidate.objectNodeId && candidate.objectNodeId.trim().length > 0
        ? candidate.objectNodeId
        : "";
  return {
    id: candidate.id,
    subjectNodeId: candidate.subjectNodeId,
    predicate: candidate.predicate,
    displayValue,
    factType: candidate.factType,
    source: candidate.source,
    sourceLabel: readSourceLabel(candidate),
    sourceIcon: readSourceIcon(candidate),
    confidence,
    confidenceLabel: confidence == null ? "" : formatConfidence(confidence),
    reviewStatus: candidate.reviewStatus,
    modelVersion: readModelIdentifier(candidate),
    ruleVersion: candidate.ruleVersion ?? null,
    datasetHashShort: readDatasetHashShort(candidate),
    timestamp: readTimestamp(candidate),
    missingTrust: isMissingTrust(candidate),
    raw: candidate,
  };
}

/** Predicate: does the candidate match the given filter chip? */
export function candidateMatchesChip(
  candidate: CandidateFact,
  chip: AiCandidateFilterChip,
): boolean {
  switch (chip) {
    case "verified":
      return (
        candidate.source === "source-truth" ||
        candidate.reviewStatus === "approved"
      );
    case "low-confidence": {
      const c = readConfidence(candidate);
      return c != null && c < AI_LOW_CONFIDENCE_THRESHOLD;
    }
    case "needs-review":
      return candidate.reviewStatus === "pending";
    default:
      return true;
  }
}

/** Predicate: does the candidate match the search query? Searches the
 *  predicate, literalValue, objectNodeId, factType, source, modelVersion,
 *  ruleVersion, subject node id, and dataset hash. */
export function candidateMatchesQuery(
  candidate: CandidateFact,
  query: string,
): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    candidate.id,
    candidate.subjectNodeId,
    candidate.predicate,
    candidate.literalValue,
    candidate.objectNodeId,
    candidate.factType,
    candidate.source,
    candidate.modelVersion,
    candidate.ruleVersion,
    candidate.reviewStatus,
    readDatasetHashShort(candidate) ?? undefined,
  ];
  return haystack.some(
    (value) => typeof value === "string" && value.toLowerCase().includes(q),
  );
}

/** Apply the search query and the active chip set cumulatively. Empty
 *  chip set means "no chip filter" (every candidate matches the chip
 *  predicate trivially). */
export function applyCandidateFilters(
  candidates: readonly CandidateFact[],
  query: string,
  chips: ReadonlySet<AiCandidateFilterChip>,
): CandidateFact[] {
  return candidates.filter((candidate) => {
    if (!candidateMatchesQuery(candidate, query)) return false;
    if (chips.size === 0) return true;
    for (const chip of chips) {
      if (!candidateMatchesChip(candidate, chip)) return false;
    }
    return true;
  });
}

// ---------------------------------------------------------------------------
// Action runners (the card delegates here so tests can drive the flow
// without a DOM)
// ---------------------------------------------------------------------------

/** Run the approve flow against the supplied client. Returns the client
 *  result so callers can render an error banner. */
export async function runApprove(
  client: AiReviewClient,
  candidateId: string,
): Promise<AiClientResult> {
  return client.approve(candidateId);
}

export async function runReject(
  client: AiReviewClient,
  candidateId: string,
): Promise<AiClientResult> {
  return client.reject(candidateId);
}

/** Run the publish flow only after the caller has resolved the
 *  confirmation dialog. The card guards this with a `Dialog`; tests
 *  resolve the confirmation directly. */
export async function runPublish(
  client: AiReviewClient,
  candidateIds: readonly string[],
  reason?: string,
): Promise<AiClientResult> {
  if (candidateIds.length === 0) {
    return { ok: false, error: "No candidates selected for publish." };
  }
  return client.publish({ candidateIds, reason });
}

/** Run the optional semantic-graph search via the client when present.
 *  Falls back to local filtering when the client does not expose a
 *  `search` method (the platform graph is a future dependency). */
export async function runSearchSemanticGraph(
  client: AiReviewClient,
  query: string,
  candidates: readonly CandidateFact[],
): Promise<readonly AiSemanticSearchHit[]> {
  if (typeof client.search === "function") {
    return client.search(query);
  }
  // Fallback: synthesize hits from local candidate matches.
  return applyCandidateFilters(candidates, query, new Set()).map(
    (candidate) => ({
      nodeId: candidate.subjectNodeId,
      factId: candidate.id,
      label:
        candidate.literalValue ?? candidate.objectNodeId ?? candidate.predicate,
      snippet: `${candidate.predicate} (${candidate.factType})`,
    }),
  );
}

// ---------------------------------------------------------------------------
// Cross-card pivot
// ---------------------------------------------------------------------------

/**
 * Dispatch the cross-card pivot to the readiness card via the WP-2
 * `viewer:tool-card-request` event. Returns the dispatched detail
 * (also handy as a synchronous test handle). When `window` is missing
 * (server-side tests) the function still returns a synthesized detail
 * so the caller can verify the contract.
 */
export function pivotToReadiness(
  selection?: Pick<
    SceneSelection,
    "indexNodeId" | "layerId" | "sceneItemId" | "featureId" | "source"
  >,
): ToolCardRequestDetail {
  const detail: ToolCardRequestDetail = {
    toolCardKind: "readiness",
    layerId: selection?.layerId,
    sceneItemId: selection?.sceneItemId,
    indexNodeId: selection?.indexNodeId,
    featureId: selection?.featureId,
    source: selection?.source ?? "tool-card",
  };
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(
        new CustomEvent<ToolCardRequestDetail>(TOOL_CARD_REQUEST_EVENT, {
          detail,
        }),
      );
    } catch {
      // ignore — return the detail unconditionally so callers can audit
    }
  }
  return detail;
}

// ---------------------------------------------------------------------------
// Convenience: derive a row VM array from a candidate array, partitioned
// into "renderable" rows and "missing-trust" rows that the card renders
// as warning banners instead.
// ---------------------------------------------------------------------------

export interface PartitionedCandidates {
  readonly rows: readonly AiCandidateRowVM[];
  readonly missingTrust: readonly AiCandidateRowVM[];
}

export function partitionCandidates(
  candidates: readonly CandidateFact[],
): PartitionedCandidates {
  const rows: AiCandidateRowVM[] = [];
  const missing: AiCandidateRowVM[] = [];
  for (const candidate of candidates) {
    const vm = toCandidateRowVM(candidate);
    if (vm.missingTrust) missing.push(vm);
    else rows.push(vm);
  }
  return { rows, missingTrust: missing };
}
